import http from '../../../services/api'
import type { APIResponse } from '../../../services/types'

const apiPath = import.meta.env.VITE_API_PATH

export type FinancialSummaryMetric = {
  amount?: number | string | null
  count?: number | string | null
  percentage_change?: number | string | null
  percentageChange?: number | string | null
  change_percentage?: number | string | null
  comparison_label?: string | null
  previous_period_label?: string | null
}

export type FinancialSummaryReport = {
  summary?: {
    total_revenue?: FinancialSummaryMetric | null
    total_payments?: FinancialSummaryMetric | null
    pending_amount?: FinancialSummaryMetric | null
    total_transactions?: FinancialSummaryMetric | null
  } | null
  revenue_trend?: Array<{
    period?: string | null
    amount?: number | string | null
  }> | null
  payment_methods_distribution?: Array<{
    payment_method?: string | null
    amount?: number | string | null
    percentage?: number | string | null
  }> | null
}

export type FinancialSummaryParams = {
  start_date: string
  end_date: string
}

export type MonthlySalesExpensesChartPoint = {
  month?: string | null
  label?: string | null
  sales?: number | string | null
  expenses?: number | string | null
}

export type MonthlySalesExpensesSummary = {
  period?: string | null
  summary?: {
    sales?: number | string | null
    expenses?: number | string | null
    net_amount?: number | string | null
  } | null
  chart?: MonthlySalesExpensesChartPoint[] | null
}

export type MonthlySalesExpensesSummaryParams = {
  period: string
}

export type CollectionsBreakdownItem = {
  key?: string | null
  label?: string | null
  amount?: number | string | null
}

export type CollectionsBreakdownReport = {
  start_date?: string | null
  end_date?: string | null
  total?: number | string | null
  collected?: number | string | null
  outstanding?: number | string | null
  percentage_collected?: number | string | null
  breakdown?: CollectionsBreakdownItem[] | null
}

export type CollectionsBreakdownParams = {
  start_date: string
  end_date: string
}

export type WeeklySalesTrendPoint = {
  period?: string | null
  amount?: number | string | null
}

export type WeeklySalesTrendReport = {
  start_date?: string | null
  end_date?: string | null
  total_sales?: number | string | null
  percentage_change?: number | string | null
  daily_sales?: WeeklySalesTrendPoint[] | null
}

export type PeriodicSalesExpensesSummary = {
  total_sales?: number | string | null
  total_collections?: number | string | null
  total_outstanding_balance?: number | string | null
  total_discounts?: number | string | null
  total_loyalty_amount?: number | string | null
  refunds?: number | string | null
  net_sales?: number | string | null
  total_transactions?: number | string | null
  total_expenses?: number | string | null
  total_costs?: number | string | null
  net_profit?: number | string | null
  cost_of_goods_sold?: number | string | null
  gross_profit?: number | string | null
  inventory_write_off_value?: number | string | null
  collection_rate?: number | string | null
  stock_adjustment_value?: number | string | null
  supplier_return_value?: number | string | null
  sales_return_value?: number | string | null
}

export type PeriodicSalesExpensesSummaryParams = {
  start_date?: string
  end_date?: string
}

async function getFinancialSummary(params: FinancialSummaryParams) {
  const queryParams = new URLSearchParams(params)

  return await http.get<APIResponse<FinancialSummaryReport> | FinancialSummaryReport>(
    `${apiPath}reports/financial-summary?${queryParams.toString()}`,
    { errorHandle: false, timeout: 30000 } as any,
  )
}

async function getMonthlySalesExpensesSummary(params: MonthlySalesExpensesSummaryParams) {
  const queryParams = new URLSearchParams(params)

  return await http.get<APIResponse<MonthlySalesExpensesSummary> | MonthlySalesExpensesSummary>(
    `${apiPath}reports/monthly-sales-expenses-summary?${queryParams.toString()}`,
    { errorHandle: false, timeout: 30000 } as any,
  )
}

async function getCollectionsBreakdown(params: CollectionsBreakdownParams) {
  const queryParams = new URLSearchParams(params)

  return await http.get<APIResponse<CollectionsBreakdownReport> | CollectionsBreakdownReport>(
    `${apiPath}reports/collections-breakdown?${queryParams.toString()}`,
    { errorHandle: false, timeout: 30000 } as any,
  )
}

async function getWeeklySalesTrend() {
  return await http.get<APIResponse<WeeklySalesTrendReport> | WeeklySalesTrendReport>(
    `${apiPath}reports/weekly-sales-trend`,
    { errorHandle: false, timeout: 30000 } as any,
  )
}

async function getPeriodicSalesExpensesSummary(params?: PeriodicSalesExpensesSummaryParams) {
  const queryParams = new URLSearchParams()

  if (params?.start_date) {
    queryParams.set('start_date', params.start_date)
  }

  if (params?.end_date) {
    queryParams.set('end_date', params.end_date)
  }

  const query = queryParams.toString()
  const url = query
    ? `${apiPath}reports/periodic-sales-expenses-summary?${query}`
    : `${apiPath}reports/periodic-sales-expenses-summary`

  return await http.get<APIResponse<PeriodicSalesExpensesSummary> | PeriodicSalesExpensesSummary>(url, {
    errorHandle: false,
    timeout: 30000,
  } as any)
}

export default {
  getFinancialSummary,
  getMonthlySalesExpensesSummary,
  getCollectionsBreakdown,
  getWeeklySalesTrend,
  getPeriodicSalesExpensesSummary,
}
